import { useEffect, useState, useMemo } from "react";
import { useLocation } from "wouter";
import {
  CalendarPlus,
  ChevronDown,
  Copy,
  Inbox,
  ListFilter,
  Plus,
} from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import ListTaskRow, { type ListTaskRowTask } from "@/components/tasks/lists/ListTaskRow/ListTaskRow";
import ListFilterPills from "@/components/tasks/options/ListFilterPills/ListFilterPills";
import ListTemplateRow, { type ListTemplateRowTemplate } from "@/components/tasks/lists/ListTemplateRow/ListTemplateRow";
import CalendarPanel, {
  CALENDAR_COLLAPSED_KEY,
  HOME_SELECTED_DATE_KEY,
  startOfMonth,
  storedCalendarDate,
} from "@/components/calendar/CalendarPanel/CalendarPanel";
import {
  monthRange,
  SimpleDayTaskList,
  HOME_DAY_SORT_KEY,
  useDaySortMode,
} from "@/components/calendar/CalendarDayTasks/CalendarDayTasks";
import CreateTaskSheet from "@/components/tasks/sheets/CreateTaskSheet/CreateTaskSheet";
import {
  createPatternAddSection,
  patternSectionToSelection,
  type PatternTemplate,
} from "@/components/tasks/patterns/PatternAddSection/PatternAddSection";
import PatternAddDialog, { type PatternAddDialogState } from "@/components/tasks/patterns/PatternAddDialog/PatternAddDialog";
import TaskFilterSheet, {
  EMPTY_TASK_FILTER,
  activeTaskFilterCount,
  matchesTaskFilter,
  type TaskFilterState,
} from "@/components/tasks/options/TaskFilterSheet/TaskFilterSheet";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import {
  nowTimeString,
  parseDateKey,
  todayKey,
} from "@/lib/time";
import { getListColor } from "@/lib/listColors";
import { listSlug } from "@/lib/listSlug";

const HOME_COLLAPSE_KEYS = {
  quickAdd: "home-quick-add-collapsed",
  lists: "home-areas-collapsed",
  inbox: "home-inbox-collapsed",
  scheduleToday: "home-schedule-today-collapsed",
  tasks: "home-area-tasks-collapsed",
  patterns: "home-area-patterns-collapsed",
} as const;

function usePersistedCollapsed(key: string) {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(key) === "true";
  });
  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(key, String(collapsed));
  }, [collapsed, key]);
  return [collapsed, () => setCollapsed(value => !value)] as const;
}

export default function Home() {
  const [, setLocation] = useLocation();
  const [selectedDate, setSelectedDate] = useState(() => storedCalendarDate(HOME_SELECTED_DATE_KEY));
  const [cursor, setCursor] = useState(() => startOfMonth(parseDateKey(storedCalendarDate(HOME_SELECTED_DATE_KEY))));
  const [calendarCollapsed, setCalendarCollapsed] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem(CALENDAR_COLLAPSED_KEY) === "true";
  });
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [createSheetDate, setCreateSheetDate] = useState<string | null>(selectedDate);
  const [inboxTitle, setInboxTitle] = useState("");
  const [todayTitle, setTodayTitle] = useState("");
  const [todayListId, setTodayListId] = useState<number | null>(null);
  const [quickAddCollapsed, toggleQuickAddCollapsed] = usePersistedCollapsed(HOME_COLLAPSE_KEYS.quickAdd);
  const [listsCollapsed, toggleListsCollapsed] = usePersistedCollapsed(HOME_COLLAPSE_KEYS.lists);
  const [inboxCollapsed, toggleInboxCollapsed] = usePersistedCollapsed(HOME_COLLAPSE_KEYS.inbox);
  const [scheduleTodayCollapsed, toggleScheduleTodayCollapsed] = usePersistedCollapsed(HOME_COLLAPSE_KEYS.scheduleToday);
  const [tasksCollapsed, toggleTasksCollapsed] = usePersistedCollapsed(HOME_COLLAPSE_KEYS.tasks);
  const [patternsCollapsed, togglePatternsCollapsed] = usePersistedCollapsed(HOME_COLLAPSE_KEYS.patterns);
  const [planningFilter, setPlanningFilter] = useState<TaskFilterState>(EMPTY_TASK_FILTER);
  const [planningFilterOpen, setPlanningFilterOpen] = useState(false);
  const [patternSheet, setPatternSheet] = useState<PatternAddDialogState | null>(null);
  const { sortMode, handleSortChange } = useDaySortMode(HOME_DAY_SORT_KEY);
  const utils = trpc.useUtils();
  const rememberLastDayQuery = trpc.preferences.rememberLastDay.useQuery();
  const rememberLastDay = rememberLastDayQuery.data?.home ?? true;

  const today = todayKey();
  const todaySummaryRange = useMemo(() => monthRange(today), [today]);

  useEffect(() => {
    if (!rememberLastDayQuery.data || rememberLastDayQuery.data.home) return;
    const next = todayKey();
    setSelectedDate(next);
    setCursor(startOfMonth(parseDateKey(next)));
  }, [rememberLastDayQuery.data]);

  useEffect(() => {
    if (typeof localStorage === "undefined" || !rememberLastDay) return;
    localStorage.setItem(HOME_SELECTED_DATE_KEY, selectedDate);
  }, [rememberLastDay, selectedDate]);

  useEffect(() => {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(CALENDAR_COLLAPSED_KEY, String(calendarCollapsed));
  }, [calendarCollapsed]);
  const listsQuery = trpc.lists.list.useQuery();
  const overviewQuery = trpc.lists.overview.useQuery({ today });
  const templatesQuery = trpc.templates.listAll.useQuery();
  const schedulableLists = useMemo(
    () =>
      ((overviewQuery.data?.lists ?? listsQuery.data) ?? [])
        .filter(list => list.kind === "inbox" || list.isGlobalActive),
    [listsQuery.data, overviewQuery.data?.lists],
  );
  const activeLists = useMemo(
    () =>
      ((overviewQuery.data?.lists ?? listsQuery.data) ?? [])
        .filter(list => list.kind === "custom" && list.isGlobalActive),
    [listsQuery.data, overviewQuery.data?.lists],
  );
  const homepagePlanningFilter = useMemo<TaskFilterState>(
    () => ({ ...planningFilter, statuses: ["pending", "doing"] }),
    [planningFilter],
  );
  const unscheduledTasksByArea = useMemo(
    () =>
      schedulableLists
        .filter(list => planningFilter.listIds.length === 0 || planningFilter.listIds.includes(list.id))
        .map(list => {
          const items = (overviewQuery.data?.tasks ?? []).filter(task =>
            task.date === null &&
            matchesTaskFilter(task, homepagePlanningFilter) &&
            (task.listId === list.id || (list.kind === "inbox" && task.listId === null)),
          );
          return { list: list, items };
        })
        .filter(group => group.items.length > 0),
    [homepagePlanningFilter, overviewQuery.data?.tasks, planningFilter.listIds, schedulableLists],
  );
  const patternsByArea = useMemo(
    () =>
      schedulableLists
        .filter(list => planningFilter.listIds.length === 0 || planningFilter.listIds.includes(list.id))
        .map(list => {
          const items = (templatesQuery.data ?? []).filter(template =>
            template.listId === list.id || (list.kind === "inbox" && template.listId === null),
          );
          return { list, items };
        })
        .filter(group => group.items.length > 0),
    [planningFilter.listIds, schedulableLists, templatesQuery.data],
  );

  useEffect(() => {
    if (todayListId !== null || schedulableLists.length === 0) return;
    const inbox = schedulableLists.find(list => list.kind === "inbox");
    setTodayListId((inbox ?? schedulableLists[0])?.id ?? null);
  }, [schedulableLists, todayListId]);

  const quickAdd = trpc.tasks.quickAdd.useMutation({
    onSuccess: () => {
      setInboxTitle("");
      utils.tasks.listInbox.invalidate();
      utils.lists.overview.invalidate();
    },
  });
  const createTodayTask = trpc.tasks.create.useMutation({
    onSuccess: () => {
      setTodayTitle("");
      utils.day.list.invalidate({ date: today });
      utils.lists.overview.invalidate();
      utils.day.monthSummary.invalidate(todaySummaryRange);
    },
  });
  const addToToday = trpc.tasks.update.useMutation({
    onSuccess: (_data, variables) => {
      const targetDate = variables.date ?? today;
      utils.lists.overview.invalidate();
      utils.day.list.invalidate({ date: targetDate });
      utils.day.monthSummary.invalidate(monthRange(targetDate));
    },
  });
  const duplicateTask = trpc.tasks.duplicate.useMutation({
    onSuccess: (_data, vars) => {
      utils.lists.overview.invalidate();
      if (vars.date) utils.day.list.invalidate({ date: vars.date });
      utils.day.monthSummary.invalidate(vars.date ? monthRange(vars.date) : todaySummaryRange);
    },
  });
  const addPatternToToday = trpc.day.addListToDay.useMutation({
    onSuccess: (_data, variables) => {
      utils.lists.overview.invalidate();
      utils.day.list.invalidate({ date: variables.date });
      utils.day.monthSummary.invalidate(monthRange(variables.date));
    },
  });

  function addPattern(
    listId: number,
    section: PatternAddDialogState["section"],
    closeOnSuccess = true,
    targetDate = today,
  ) {
    return addPatternToToday.mutateAsync({
      date: targetDate,
      listId,
      fallbackStartTime: nowTimeString(),
      selections: [patternSectionToSelection({ ...section, selected: true })],
    }).then(result => {
      if (closeOnSuccess) setPatternSheet(null);
      return result;
    });
  }

  function openPattern(list: HomeArea, pattern: PatternTemplate) {
    setPatternSheet({
      listId: list.id,
      listName: list.name,
      listColor: list.color,
      listBgColor: list.bgColor ?? null,
      section: createPatternAddSection(pattern, nowTimeString(), {
        selected: true,
        expanded: true,
      }),
    });
  }

  function togglePlanningListFilter(listId: number) {
    setPlanningFilter(current => ({
      ...current,
      listIds: current.listIds.length === 1 && current.listIds[0] === listId ? [] : [listId],
    }));
  }

  function openCreateSheet(date: string | null) {
    setCreateSheetDate(date);
    setCreateSheetOpen(true);
  }

  function addInboxTask(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = inboxTitle.trim();
    if (!title || quickAdd.isPending) return;
    quickAdd.mutate({ title });
  }

  function addTodayTask(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = todayTitle.trim();
    if (!title || todayListId === null || createTodayTask.isPending) return;
    createTodayTask.mutate({
      title,
      date: today,
      listId: todayListId,
      startTime: null,
      alertEnabled: false,
    });
  }

  return (
    <AppShell>
      <div className="ui-page-wrapper container max-w-3xl py-8 sm:py-12 space-y-8">
        <CalendarPanel
          selectedDate={selectedDate}
          cursor={cursor}
          collapsed={calendarCollapsed}
          onSelectedDateChange={setSelectedDate}
          onCursorChange={setCursor}
          onCollapsedChange={setCalendarCollapsed}
        />

        <SimpleDayTaskList
          date={selectedDate}
          sortMode={sortMode}
          onSortChange={handleSortChange}
          activeOnly
          showFilters={false}
          onDateClick={() => setLocation(`/calendar?day=${selectedDate}`)}
        />

        <CollapsibleHomeSection
          id="quick-add-heading"
          title="Quick add"
          collapsed={quickAddCollapsed}
          onToggle={toggleQuickAddCollapsed}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <QuickInboxCard
              title={inboxTitle}
              pending={quickAdd.isPending}
              collapsed={inboxCollapsed}
              onToggleCollapse={toggleInboxCollapsed}
              onTitleChange={setInboxTitle}
              onSubmit={addInboxTask}
            />
            <ScheduleTodayCard
              title={todayTitle}
              listId={todayListId}
              lists={schedulableLists}
              pending={createTodayTask.isPending}
              collapsed={scheduleTodayCollapsed}
              onToggleCollapse={toggleScheduleTodayCollapsed}
              onTitleChange={setTodayTitle}
              onAreaChange={setTodayListId}
              onSubmit={addTodayTask}
            />
          </div>
        </CollapsibleHomeSection>

        <PlanningHomeSection
          collapsed={listsCollapsed}
          onToggle={toggleListsCollapsed}
          filterActiveCount={activeTaskFilterCount(planningFilter)}
          onFilterClick={() => setPlanningFilterOpen(true)}
        >
          <TaskFilterSheet
            open={planningFilterOpen}
            onOpenChange={setPlanningFilterOpen}
            value={planningFilter}
            onChange={setPlanningFilter}
            lists={schedulableLists.map(list => ({ id: list.id, name: list.name, color: list.color }))}
            showSchedule={false}
            title="Filter planning"
            description="Narrow tasks and patterns by list or task status."
          />
          <ListFilterPills
            lists={activeLists}
            loading={listsQuery.isLoading}
            selectedListIds={planningFilter.listIds.filter((id): id is number => id !== null)}
            onSelect={togglePlanningListFilter}
            purpose="planning"
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <AreaShortcutCard
              title="Tasks"
              groups={unscheduledTasksByArea}
              loading={overviewQuery.isLoading}
              collapsed={tasksCollapsed}
              onToggleCollapse={toggleTasksCollapsed}
              empty="No tasks match this filter."
              display="tasks"
              onOpen={setLocation}
              onTaskAddToToday={task =>
                addToToday.mutate({
                  id: task.id,
                  date: selectedDate,
                  startTime: null,
                  clientTimestamp: new Date().toISOString(),
                })
              }
              onTaskDuplicate={task =>
                duplicateTask.mutate({
                  id: task.id,
                  date: task.date ?? null,
                })
              }
            />
            <AreaShortcutCard
              title="Patterns"
              groups={patternsByArea}
              loading={templatesQuery.isLoading}
              collapsed={patternsCollapsed}
              onToggleCollapse={togglePatternsCollapsed}
              empty="No patterns match this filter."
              display="patterns"
              onOpen={setLocation}
              onPatternOpen={openPattern}
              onPatternAddToToday={(list, pattern) =>
                addPattern(
                  list.id,
                  createPatternAddSection(pattern, nowTimeString(), {
                    selected: true,
                    expanded: false,
                  }),
                  false,
                  selectedDate,
                )
              }
            />
          </div>
        </PlanningHomeSection>

        <Button
          onClick={() => openCreateSheet(selectedDate)}
          size="lg"
          className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full shadow-lg gap-0 p-0"
          aria-label="Add task"
        >
          <Plus className="h-5 w-5" />
        </Button>
        <CreateTaskSheet
          open={createSheetOpen}
          onOpenChange={setCreateSheetOpen}
          date={createSheetDate}
        />
        <PatternAddDialog
          value={patternSheet}
          pending={addPatternToToday.isPending}
          onChange={setPatternSheet}
          onClose={() => setPatternSheet(null)}
          onAdd={sheet => addPattern(sheet.listId, sheet.section)}
        />
      </div>
    </AppShell>
  );
}

type HomeArea = {
  id: number;
  name: string;
  color: string;
  bgColor?: string | null;
  kind: "inbox" | "custom";
  isGlobalActive: boolean;
};


function PlanningHomeSection({
  collapsed,
  onToggle,
  filterActiveCount,
  onFilterClick,
  children,
}: {
  collapsed: boolean;
  onToggle: () => void;
  filterActiveCount: number;
  onFilterClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby="planning-heading" className="space-y-4 border-t border-border/70 pt-8 sm:pt-10">
      <div className="flex items-start justify-between gap-4">
        <button
          type="button"
          onClick={onToggle}
          className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={!collapsed}
          aria-controls="planning-content"
        >
          <span className="flex items-center gap-3">
            <span>
              <span id="planning-heading" className="block text-3xl font-semibold tracking-tight sm:text-4xl">Planning</span>
              <span className="mt-1 block text-sm text-muted-foreground">Browse tasks and patterns to add to your day.</span>
            </span>
            <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform ${collapsed ? "-rotate-90" : ""}`} aria-hidden />
          </span>
        </button>
        {!collapsed && (
          <button
            type="button"
            onClick={onFilterClick}
            className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-full transition-colors ${filterActiveCount > 0 ? "bg-foreground text-background" : "bg-accent text-muted-foreground hover:text-foreground"}`}
            aria-label={filterActiveCount > 0 ? `Edit ${filterActiveCount} planning filters` : "Filter planning"}
            title="Filter planning"
          >
            <ListFilter className="h-4 w-4" />
            {filterActiveCount > 0 && <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-background px-1 text-[10px] font-bold text-foreground shadow-sm">{filterActiveCount}</span>}
          </button>
        )}
      </div>
      {!collapsed && <div id="planning-content" className="space-y-4">{children}</div>}
    </section>
  );
}

function CollapsibleHomeSection({
  id,
  title,
  collapsed,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <button
        type="button"
        onClick={onToggle}
        className="block text-left text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground transition-colors"
        aria-expanded={!collapsed}
        aria-controls={`${id}-content`}
      >
        <h2 id={id}>{title}</h2>
      </button>
      {!collapsed && (
        <div id={`${id}-content`} className="space-y-3">
          {children}
        </div>
      )}
    </section>
  );
}

function QuickInboxCard({
  title,
  pending,
  collapsed,
  onToggleCollapse,
  onTitleChange,
  onSubmit,
}: {
  title: string;
  pending: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onTitleChange: (title: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-xl border border-border/60 bg-card p-3 space-y-3"
      aria-label="Inbox quick capture"
    >
      <CardHeader
        icon={<Inbox className="h-4 w-4" />}
        title="Inbox"
        collapsed={collapsed}
        onToggle={onToggleCollapse}
        controlsId="home-inbox-content"
      />
      {!collapsed && (
        <div id="home-inbox-content" className="flex gap-2">
          <input
            value={title}
            onChange={event => onTitleChange(event.target.value)}
            placeholder="Quick thought"
            className="min-w-0 flex-1 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm outline-none focus:border-foreground/40"
          />
          <Button type="submit" size="sm" disabled={!title.trim() || pending}>
            Add
          </Button>
        </div>
      )}
    </form>
  );
}
function ScheduleTodayCard({
  title,
  listId,
  lists,
  pending,
  collapsed,
  onToggleCollapse,
  onTitleChange,
  onAreaChange,
  onSubmit,
}: {
  title: string;
  listId: number | null;
  lists: HomeArea[];
  pending: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onTitleChange: (title: string) => void;
  onAreaChange: (id: number | null) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  const selectedArea = lists.find(list => list.id === listId) ?? lists[0] ?? null;
  const selectedColors = getListColor(selectedArea?.color);
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-xl border border-border/60 bg-card p-3 space-y-3"
      aria-label="Schedule today"
    >
      <CardHeader
        icon={<CalendarPlus className="h-4 w-4" />}
        title="Schedule today"
        collapsed={collapsed}
        onToggle={onToggleCollapse}
        controlsId="home-schedule-today-content"
        action={!collapsed ? (
          <label
            className="relative inline-flex max-w-36 shrink-0 items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-[0.14em] hover:opacity-80 transition-opacity"
            style={{
              background: `color-mix(in oklch, ${selectedColors.base} 18%, transparent)`,
              color: selectedColors.ink,
            }}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: selectedColors.base }}
              aria-hidden
            />
            <span className="truncate">{selectedArea?.name ?? "Inbox"}</span>
            <select
              value={listId ?? ""}
              onChange={event => onAreaChange(event.target.value ? Number(event.target.value) : null)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label="Choose list"
            >
              {lists.map(list => (
                <option key={list.id} value={list.id}>
                  {list.name}
                </option>
              ))}
            </select>
          </label>
        ) : undefined}
      />
      {!collapsed && (
        <div id="home-schedule-today-content" className="flex gap-2">
          <input
            value={title}
            onChange={event => onTitleChange(event.target.value)}
            placeholder="Task for today"
            className="min-w-0 flex-1 rounded-lg border border-border/70 bg-background px-3 py-2 text-sm outline-none focus:border-foreground/40"
          />
          <Button type="submit" size="sm" disabled={!title.trim() || listId === null || pending}>
            Add
          </Button>
        </div>
      )}
    </form>
  );
}
function AreaShortcutCard({
  title,
  groups,
  loading,
  collapsed,
  onToggleCollapse,
  empty,
  display,
  onOpen,
  onTaskAddToToday,
  onTaskDuplicate,
  onPatternAddToToday,
  onPatternOpen,
}: {
  title: string;
  groups:
    | Array<{ list: HomeArea; items: ListTaskRowTask[] }>
    | Array<{ list: HomeArea; items: ListTemplateRowTemplate[] }>;
  loading: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  empty: string;
  display: "tasks" | "patterns";
  onOpen: (href: string) => void;
  onTaskAddToToday?: (task: ListTaskRowTask) => void;
  onTaskDuplicate?: (task: ListTaskRowTask) => void;
  onPatternAddToToday?: (list: HomeArea, pattern: PatternTemplate) => void;
  onPatternOpen?: (list: HomeArea, pattern: PatternTemplate) => void;
}) {
  const controlsId = "home-" + display + "-content";
  return (
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
      <CardHeader
        icon={<Copy className="h-4 w-4" />}
        title={title}
        collapsed={collapsed}
        onToggle={onToggleCollapse}
        controlsId={controlsId}
      />
      {!collapsed && (
        <div id={controlsId} className="space-y-1.5">
          {loading ? (
            <>
              <span className="block h-8 rounded-lg bg-muted/50 animate-pulse" />
              <span className="block h-8 rounded-lg bg-muted/50 animate-pulse" />
            </>
          ) : groups.length === 0 ? (
            <p className="text-xs text-muted-foreground">{empty}</p>
          ) : (
            groups.map(({ list, items }) => {
              const color = getListColor(list.color);
              return (
                <div key={list.id} className="space-y-1">
                  <button
                    type="button"
                    onClick={() =>
                      onOpen(`/lists?list=${encodeURIComponent(listSlug(list.name))}&display=${display}`)
                    }
                    className="flex h-7 w-full items-center justify-between gap-2 rounded-lg px-2 text-left text-[11px] font-semibold uppercase tracking-[0.14em] hover:bg-accent/25"
                    style={{ color: color.ink }}
                  >
                    <span className="min-w-0 inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color.base }} />
                      <span className="truncate">{list.name}</span>
                    </span>
                    <span className="shrink-0 tabular-nums text-muted-foreground">{items.length}</span>
                  </button>
                  <div className="overflow-hidden rounded-lg border border-border/50 bg-card">
                    {items.map(item => (
                      display === "tasks" ? (
                        <ListTaskRow
                          key={item.id}
                          task={item as ListTaskRowTask}
                          variant="simple"
                          showSchedule={false}
                          onOpen={() => onOpen(`/task/${item.id}`)}
                          onAddToToday={() => onTaskAddToToday?.(item as ListTaskRowTask)}
                          onDuplicate={() => onTaskDuplicate?.(item as ListTaskRowTask)}
                        />
                      ) : (
                        <ListTemplateRow
                          key={item.id}
                          template={item as ListTemplateRowTemplate}
                          variant="simple"
                          listColor={list.color}
                          listBgColor={list.bgColor ?? null}
                          onOpen={() =>
                            onPatternOpen
                              ? onPatternOpen(list, item as PatternTemplate)
                              : onOpen(`/lists?list=${encodeURIComponent(listSlug(list.name))}&display=patterns`)
                          }
                          onAddToToday={() => onPatternAddToToday?.(list, item as PatternTemplate)}
                        />
                      )
                    ))}

                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
function CardHeader({
  icon,
  title,
  description,
  action,
  collapsed,
  onToggle,
  controlsId,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  collapsed?: boolean;
  onToggle?: () => void;
  controlsId?: string;
}) {
  const heading = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-accent text-foreground">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold">{title}</span>
        {description && (
          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{description}</span>
        )}
      </span>
    </>
  );
  return (
    <div className="flex items-start gap-2">
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          aria-expanded={!collapsed}
          aria-controls={controlsId}
        >
          {heading}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${collapsed ? "-rotate-90" : ""}`} aria-hidden />
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-3">{heading}</div>
      )}
      {action}
    </div>
  );
}
